import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import AppSidebar from "./AppSidebar"
import Footer from "./Footer"
import ShortcutsDialog from "./ShortcutsDialog"
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts"
import { useLocation, Link } from "react-router-dom"
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { useAuthStore } from "../../stores/authStore"
import { useBreadcrumbStore } from "../../stores/breadcrumbStore"
import { LifeBuoy } from "lucide-react"
import { RouteTransition } from "@/components/page"
import { cn } from "@/lib/utils"

// Inicio, Mis cursos y Ayuda aprovechan todo el ancho de pantallas grandes.
// El resto (por ejemplo CCCD) conserva el ancho de lectura.
const WIDE_ROUTES = ["/dashboard", "/mis-cursos", "/ayuda", "/certificados"]
// El briefing de categoría de CCCD (/cccd/:evento) también; los listados de competidores no.
const isWide = (pathname: string) =>
  WIDE_ROUTES.includes(pathname) || (pathname.startsWith("/cccd/") && !pathname.startsWith("/cccd/competidores"))

interface LayoutProps {
  children: React.ReactNode
}

interface Crumb {
  label: string
  to?: string
}

// Explicit trail per route in App.tsx's DashboardLayout group. Segments with
// no route of their own (/grupo, bare ids) are plain text, never links, and an
// unknown path yields no trail instead of a raw URL segment.
function buildCrumbs(pathname: string, labels: Record<string, string>): Crumb[] {
  const parts = pathname.split("/").filter(Boolean)
  const [root, second, eventId, third, teamId] = parts
  const label = (path: string, fallback: string) => labels[path] || fallback

  switch (root) {
    case "dashboard":
      return [{ label: "Inicio" }]
    case "mis-cursos":
      return [{ label: "Mis cursos" }]
    case "ayuda":
      return [{ label: "Ayuda" }]
    case "certificados":
      return [{ label: "Certificados" }]
    case "grupo":
      return second === "certificados" ? [{ label: "Grupo" }, { label: "Certificados" }] : []
    case "cccd": {
      if (!second) return [{ label: "CCCD" }]
      if (second !== "competidores") {
        return [{ label: "CCCD", to: "/cccd" }, { label: label(`/cccd/${second}`, "Evento") }]
      }
      if (!eventId) return [{ label: "CCCD", to: "/cccd" }, { label: "Competidores" }]
      const eventPath = `/cccd/competidores/${eventId}`
      const trail: Crumb[] = [
        { label: "CCCD", to: "/cccd" },
        { label: "Competidores", to: "/cccd/competidores" },
      ]
      if (third === "equipos" && teamId) {
        trail.push({ label: label(eventPath, "Evento"), to: eventPath })
        trail.push({ label: label(`${eventPath}/equipos/${teamId}`, "Equipo") })
      } else {
        trail.push({ label: label(eventPath, "Evento") })
      }
      return trail
    }
    default:
      return []
  }
}

export default function DashboardLayout({ children }: LayoutProps) {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const breadcrumbLabels = useBreadcrumbStore((s) => s.labels)
  const crumbs = buildCrumbs(location.pathname, breadcrumbLabels)
  const isStudent = useAuthStore((s) => s.sessionType !== "group")
  const shortcuts = useKeyboardShortcuts(isAuthenticated, isStudent)

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-1 flex-col overflow-hidden">
          {/* Top bar */}
          <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center justify-between gap-2 glass border-x-0 border-t-0 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="-ml-1 h-11 w-11 sm:h-8 sm:w-8 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground" />
              <Separator orientation="vertical" className="h-4 opacity-30" />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      render={
                        <Link to={isAuthenticated ? "/dashboard" : "/"} className="text-xs text-muted-foreground hover:text-primary" />
                      }
                    >
                      SateLab Academy
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {crumbs.map((crumb, index) => (
                    <div key={`${index}-${crumb.label}`} className="flex items-center">
                      <BreadcrumbSeparator className="text-muted-foreground/60" />
                      <BreadcrumbItem>
                        {index === crumbs.length - 1 ? (
                          <BreadcrumbPage className="max-w-[16rem] truncate text-xs font-semibold text-foreground">
                            {crumb.label}
                          </BreadcrumbPage>
                        ) : crumb.to ? (
                          <BreadcrumbLink
                            render={
                              <Link
                                to={crumb.to}
                                className="max-w-[16rem] truncate text-xs text-muted-foreground hover:text-primary"
                              />
                            }
                          >
                            {crumb.label}
                          </BreadcrumbLink>
                        ) : (
                          <span className="max-w-[16rem] truncate text-xs text-muted-foreground">{crumb.label}</span>
                        )}
                      </BreadcrumbItem>
                    </div>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Link
                to="/ayuda"
                aria-label="Ayuda"
                title="Ayuda"
                className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-[color,background-color,transform] duration-(--dur-fast) ease-(--ease-out-expo) hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 motion-press group/help sm:h-9 sm:w-9"
              >
                <LifeBuoy className="h-4 w-4 transition-transform duration-(--dur-base) ease-(--ease-out-expo) group-hover/help:rotate-12 motion-reduce:transition-none motion-reduce:group-hover/help:rotate-0" aria-hidden="true" />
              </Link>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className={cn("mx-auto", isWide(location.pathname) ? "max-w-[110rem]" : "max-w-7xl")}>
              <RouteTransition>{children}</RouteTransition>
            </div>
          </main>

          <Footer wide={isWide(location.pathname)} />
        </SidebarInset>
        <ShortcutsDialog
          open={shortcuts.open}
          onOpenChange={shortcuts.setOpen}
          enabled={shortcuts.enabled}
          onEnabledChange={shortcuts.setEnabled}
          isStudent={isStudent}
        />
      </div>
    </SidebarProvider>
  )
}
